import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Descriptions, Tag, Space, Spin, message } from 'antd';
import CopyInfoButton from '../../components/CopyInfoButton';
import { getWarehouseDetail } from '../../api/warehouse';
import type { WarehouseDetail as WarehouseDetailType } from '../../types/warehouse';

const WarehouseDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [warehouse, setWarehouse] = useState<WarehouseDetailType | null>(null);

  useEffect(() => {
    const fetchWarehouseDetail = async () => {
      try {
        const { data } = await getWarehouseDetail(Number(id));
        setWarehouse(data);
        // 写入 TabsManager 可读取的标题缓存（基于完整路径）
        try {
          const basePath = window.location.pathname.split('?')[0];
          if (data?.name) {
            sessionStorage.setItem(`tab_title_warehouse:${basePath}`, data.name);
          }
        } catch {}
      } catch (error) {
        console.error('获取仓库详情失败:', error);
        message.error('获取仓库详情失败');
      } finally {
        setLoading(false);
      }
    };

    fetchWarehouseDetail();
  }, [id]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <Spin size="large" />
      </div>
    );
  }

  if (!warehouse) {
    return (
      <div className="p-6">
        <Card>
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-600">仓库不存在</h2>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6">
      <Card
        title={
          <Space>
            <span>仓库详情</span>
            {warehouse && (
              <CopyInfoButton
                style={{ backgroundColor: '#60a5fa', borderColor: '#60a5fa', color: '#fff' }}
                onClick={async () => {
                  try {
                    const lines: string[] = [
                      `仓库名称: ${warehouse.name || ''}`,
                      `仓库代码: ${warehouse.code || ''}`,
                      `所在国家: ${warehouse.country_display || ''}`,
                      `公司名称: ${warehouse.company_name || ''}`,
                      `详细地址: ${(warehouse.address_line1 || '') + (warehouse.address_line2 ? ('\n' + warehouse.address_line2) : '')}`,
                      `城市: ${warehouse.city || ''}`,
                      `州/省: ${warehouse.state || ''}`,
                      `邮编: ${warehouse.postal_code || ''}`,
                      `联系人: ${warehouse.contact_name || ''}`,
                      `联系电话: ${warehouse.contact_phone || ''}`,
                      `联系邮箱: ${warehouse.contact_email || ''}`,
                      `工作时间: ${warehouse.working_hours || ''}`,
                      `入库方式: ${warehouse.inbound_methods_display || ''}`,
                      `出库方式: ${warehouse.outbound_methods_display || ''}`,
                      `卸货平台: ${warehouse.has_loading_dock ? '有' : '无'}`,
                      `可发货物流渠道: ${warehouse.shipping_channels || ''}`,
                      `入库预约要求: ${warehouse.inbound_appointment_requirements || '无'}`,
                      `出库预约要求: ${warehouse.outbound_appointment_requirements || '无'}`,
                      `备注信息: ${warehouse.notes || '无'}`,
                    ];
                    const text = lines.join('\n');
                    if (navigator.clipboard && navigator.clipboard.writeText) {
                      await navigator.clipboard.writeText(text);
                    } else {
                      const ta = document.createElement('textarea');
                      ta.value = text;
                      ta.style.position = 'fixed';
                      ta.style.opacity = '0';
                      document.body.appendChild(ta);
                      ta.select();
                      document.execCommand('copy');
                      document.body.removeChild(ta);
                    }
                    message.success('仓库信息已复制到剪贴板');
                  } catch (e) {
                    message.error('复制失败，请重试');
                  }
                }}
              />
            )}
          </Space>
        }
      >
        <Descriptions bordered column={2}>
          <Descriptions.Item label="仓库名称" span={2}>
            {warehouse.name}
          </Descriptions.Item>
          <Descriptions.Item label="仓库代码">
            {warehouse.code}
          </Descriptions.Item>
          <Descriptions.Item label="所在国家">
            {warehouse.country_display}
          </Descriptions.Item>
          <Descriptions.Item label="公司名称" span={2}>
            {warehouse.company_name}
          </Descriptions.Item>
          <Descriptions.Item label="详细地址" span={2}>
            {warehouse.address_line1}
            {warehouse.address_line2 && <br />}
            {warehouse.address_line2}
          </Descriptions.Item>
          <Descriptions.Item label="城市">
            {warehouse.city}
          </Descriptions.Item>
          <Descriptions.Item label="州/省">
            {warehouse.state}
          </Descriptions.Item>
          <Descriptions.Item label="邮编">
            {warehouse.postal_code}
          </Descriptions.Item>
          <Descriptions.Item label="联系人">
            {warehouse.contact_name}
          </Descriptions.Item>
          <Descriptions.Item label="联系电话">
            {warehouse.contact_phone}
          </Descriptions.Item>
          <Descriptions.Item label="联系邮箱">
            {warehouse.contact_email}
          </Descriptions.Item>
          <Descriptions.Item label="工作时间">
            {warehouse.working_hours}
          </Descriptions.Item>
          <Descriptions.Item label="入库方式">
            <Space>
              {warehouse.inbound_methods_display.split(', ').map((method, index) => (
                <Tag key={index} color="blue">{method}</Tag>
              ))}
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="出库方式">
            <Space>
              {warehouse.outbound_methods_display.split(', ').map((method, index) => (
                <Tag key={index} color="green">{method}</Tag>
              ))}
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="卸货平台">
            <Tag color={warehouse.has_loading_dock ? 'success' : 'default'}>
              {warehouse.has_loading_dock ? '有' : '无'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="可发货物流渠道">
            {warehouse.shipping_channels}
          </Descriptions.Item>
          <Descriptions.Item label="入库预约要求">
            {warehouse.inbound_appointment_requirements || '无'}
          </Descriptions.Item>
          <Descriptions.Item label="出库预约要求">
            {warehouse.outbound_appointment_requirements || '无'}
          </Descriptions.Item>
          <Descriptions.Item label="备注信息" span={2}>
            {warehouse.notes || '无'}
          </Descriptions.Item>
        </Descriptions>
      </Card>
    </div>
  );
};

export default WarehouseDetail; 